/**
 * useLeagueModeNav — v2.5.124 (Phase 11)
 * ─────────────────────────────────────────────────────────────────
 * Returns the left-sidebar nav configuration for the user's
 * current "mode":
 *
 *   • Team mode (default): the four layouts build their nav
 *     items the way they always have (Dashboard, Schedule, Stats,
 *     Team Settings, Leagues, etc.).
 *   • League mode: the user is on a /league/:leagueId/* route.
 *     The sidebar replaces team items with the league sub-nav
 *     (Dashboard, Members, Settings, Venues, Divisions, Seasons,
 *     Players, Schedule, Officials, Standings, Announcements).
 *
 * Why a hook instead of duplicating logic across four layouts:
 *   • DesktopLayout, TabletLayout, MobileLayout, CapacitorLayout
 *     each build their own nav. Adding league-mode in each one
 *     risks drift; centralizing it here keeps the four layouts
 *     consistent.
 *   • The page permission gate (manage_announcements etc.) is
 *     keyed off the same membership hook as LeagueSubNav so the
 *     left sidebar and the in-page tab bar agree on what's
 *     visible.
 *
 * Returns an object:
 *   {
 *     inLeagueMode:  boolean,        // is current path /league/:id/* ?
 *     leagueId:      string | null,  // the :id segment, when matched
 *     leagueNavItems: Array<NavItem> | null,
 *     league:        { id, name } | null,  // basic league info (or null while loading)
 *   }
 *
 * The component using this hook is responsible for choosing
 * between leagueNavItems and its own team-mode items based on
 * inLeagueMode. The hook never throws and degrades to "team
 * mode" if anything goes wrong (network failure, etc.).
 *
 * NavItem shape matches what each layout already expects:
 *   { to: string, icon: LucideIcon, label: string, end?: boolean }
 *
 * useLocation is from react-router-dom v6; useLeagueMembership
 * hook is the same one LeagueSubNav uses for permissions.
 */

import { useLocation } from 'react-router-dom';
import { useEffect, useState } from 'react';
import {
  LayoutDashboard, Users, Settings, MapPin, Layers,
  Calendar, UserPlus, CalendarDays, Shield, Trophy, Megaphone,
  ClipboardList, MessageSquare, Crown,
} from 'lucide-react';
import api from '../services/api.js';
import { useLeagueMembership } from './useLeagueMembership.js';

/**
 * Extract :leagueId from /league/:id/* paths. Returns null when
 * we're not on a league route at all (or on the league list
 * page /leagues, which is team-mode).
 *
 * Examples:
 *   /league/abc/dashboard       → 'abc'
 *   /league/abc                 → 'abc'
 *   /leagues                    → null  (the list page)
 *   /team/xyz/games             → null
 */
function matchLeagueId(pathname) {
  // `^/league/` (singular) to avoid matching /leagues (the list page).
  // [^/?#] disallows query/hash leaking into the captured id.
  const m = /^\/league\/([^/?#]+)/.exec(pathname || '');
  return m ? m[1] : null;
}

export function useLeagueModeNav() {
  const location = useLocation();
  const leagueId = matchLeagueId(location.pathname);
  const inLeagueMode = !!leagueId;
  const { isMember, isManager, can, loading: membershipLoading } = useLeagueMembership(leagueId);

  // Load minimal league info (name, slug) for the header pill /
  // sidebar caption. Done as a side effect so the four layouts
  // don't each duplicate this fetch.
  //
  // Note: this duplicates useLeagueMembership's own fetch of the
  // same endpoint. Both hooks call GET /leagues/:id; we use this
  // one for the .name field and useLeagueMembership uses it for
  // .member_role + .permissions_json. The second request usually
  // hits the membership cache and returns near-instantly; the
  // real cost would be roughly +1 DB roundtrip on the very first
  // page load in league mode. Acceptable for now. A future
  // refactor could expose `league` from useLeagueMembership's
  // cache and consume it here.
  const [league, setLeague] = useState(null);
  useEffect(() => {
    if (!leagueId) { setLeague(null); return; }
    let cancelled = false;
    api.get(`/leagues/${leagueId}`)
      .then(({ data }) => { if (!cancelled) setLeague(data?.league || null); })
      .catch(() => { if (!cancelled) setLeague(null); });
    return () => { cancelled = true; };
  }, [leagueId]);

  if (!inLeagueMode) {
    return { inLeagueMode: false, leagueId: null, leagueNavItems: null, league: null };
  }

  // Mirror LeagueSubNav's visibility rules so the sidebar and the
  // in-page sub-nav stay in lockstep. Anyone with `isMember` sees
  // every item; the Settings page additionally gates on
  // manage_league_settings.
  const items = [
    { to: `/league/${leagueId}/dashboard`,     icon: LayoutDashboard, label: 'Dashboard', end: false, visible: isMember },
    { to: `/league/${leagueId}/members`,       icon: Users,           label: 'Members',                visible: isMember },
    { to: `/league/${leagueId}/settings`,      icon: Settings,        label: 'Settings',
      visible: isManager || can('manage_league_settings') },
    { to: `/league/${leagueId}/venues`,        icon: MapPin,          label: 'Venues',                 visible: isMember },
    { to: `/league/${leagueId}/divisions`,     icon: Layers,          label: 'Divisions',              visible: isMember },
    { to: `/league/${leagueId}/seasons`,       icon: Calendar,        label: 'Seasons',                visible: isMember },
    { to: `/league/${leagueId}/players`,       icon: UserPlus,        label: 'Players',                visible: isMember },
    { to: `/league/${leagueId}/schedule`,      icon: CalendarDays,    label: 'Schedule',               visible: isMember },
    { to: `/league/${leagueId}/officials`,     icon: Shield,          label: 'Officials',              visible: isMember },
    { to: `/league/${leagueId}/standings`,     icon: Trophy,          label: 'Standings',              visible: isMember },
    // Phase F (league gap-closure) — playoff brackets.
    { to: `/league/${leagueId}/playoffs`,      icon: Crown,           label: 'Playoffs',               visible: isMember },
    { to: `/league/${leagueId}/announcements`, icon: Megaphone,       label: 'Announcements',          visible: isMember },
    // Phase B (league gap-closure, 2026-06-09) — cross-team coaches
    // channel. Shown to all members; the page itself explains
    // access (coaches + staff) and lets managers enable the channel.
    // Coaches who are NOT league members reach it via the team-side
    // entry point instead (their team's chat page).
    { to: `/league/${leagueId}/chat`,          icon: MessageSquare,   label: 'Chat',                   visible: isMember },
    // v2.5.133 — Phase 9A. Registration config (form builder +
    // waivers). Gated on manage_registration so a coach without
    // that permission doesn't see it. Public form ships in 9B.
    { to: `/league/${leagueId}/registration`,  icon: ClipboardList,   label: 'Registration',
      visible: isManager || can('manage_registration') },
    // Public pages overhaul P4 (2026-06-09) — blog/coach-profile
    // authoring + public settings for the redesigned /l/:slug site.
    { to: `/league/${leagueId}/public-site`,   icon: Megaphone,       label: 'Public site',
      visible: isManager || can('manage_public_page') },
  ];

  // visible=false items don't render. Keeps the sidebar tidy when
  // a viewer has no settings access, for example.
  const leagueNavItems = items.filter(i => i.visible).map(({ visible, ...rest }) => rest);

  // Edge case: user visits /league/abc/dashboard for a league they
  // aren't a member of. Wait for membership to load before deciding
  // — otherwise the first render returns empty (default state) and
  // we fall back to team mode, then the second render (after the
  // membership fetch resolves) flips back. The flicker would be
  // confusing. Once loaded: if not a member, return team-mode (the
  // page itself will 403); during load, still render league mode
  // with the items the layout will fill in once isMember resolves.
  if (!membershipLoading && leagueNavItems.length === 0) {
    return { inLeagueMode: false, leagueId, leagueNavItems: null, league };
  }

  return { inLeagueMode: true, leagueId, leagueNavItems, league };
}
